import React, { useEffect, useState } from 'react';
import { Button, Modal, message, Input, Pagination, Spin, InputNumber } from "antd";
import { Helmet } from "react-helmet-async";
import {
    Search,
    Filter,
    RefreshCw,
    Eye,
    Check,
    X,
    Calendar,
    User,
    Package,
    Tag,
    FileText,
    Image as ImageIcon
} from 'lucide-react';
import { getAdminInvestmentTransactionsList, approvePaymentRequest_Admin, rejectPaymentRequest_Admin } from "@/api/Admin/payment";
import { PaymentAdminAPI } from "@/api/Admin/type";
import dayjs from 'dayjs';

const { TextArea } = Input;

const StatusBadge = ({ status }: { status: string }) => {
    const statusLower = status?.toLowerCase() || '';
    const stylesMap: Record<string, string> = {
        pending: 'bg-yellow-100 text-yellow-800',
        approved: 'bg-emerald-100 text-emerald-800',
        completed: 'bg-emerald-100 text-emerald-800',
        rejected: 'bg-red-100 text-red-800',
        cancelled: 'bg-gray-100 text-gray-800',
    };

    const labelsMap: Record<string, string> = {
        pending: 'Đang chờ',
        approved: 'Đã duyệt',
        completed: 'Thành công',
        rejected: 'Bị từ chối',
        cancelled: 'Đã hủy',
    };

    return (
        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${stylesMap[statusLower] || 'bg-gray-100 text-gray-800'}`}>
            {labelsMap[statusLower] || status}
        </span>
    );
};

const formatCurrency = (amount: any, currency: string = 'đ') => {
    const val = typeof amount === 'string' ? parseFloat(amount) : amount;
    return new Intl.NumberFormat('vi-VN').format(val || 0) + ' ' + currency;
};

export default function InvestmentTransactionAdmin() {
    const [data, setData] = useState<PaymentAdminAPI | null>(null);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [searchCode, setSearchCode] = useState('');

    const [detailModal, setDetailModal] = useState<{ open: boolean, data: any | null }>({ open: false, data: null });
    const [actionModal, setActionModal] = useState<{ open: boolean, type: 'approve' | 'reject' | null, id: number | null }>({ open: false, type: null, id: null });
    const [adminNote, setAdminNote] = useState('');
    const [receivedAmount, setReceivedAmount] = useState<number>(0);

    const fetchData = () => {
        setLoading(true);
        getAdminInvestmentTransactionsList({
            page,
            per_page: pageSize,
            code: searchCode
        }).then(res => {
            setData(res);
        }).catch(() => {
            message.error('Không thể tải danh sách giao dịch đầu tư');
        }).finally(() => {
            setLoading(false);
        });
    };

    useEffect(() => {
        fetchData();
    }, [page, pageSize]);

    const handleSearch = () => {
        setPage(1);
        fetchData();
    };

    const handleOpenDetail = (item: any) => {
        setDetailModal({ open: true, data: item });
    };

    const handleOpenAction = (type: 'approve' | 'reject', id: number, defaultAmount?: number) => {
        setActionModal({ open: true, type, id });
        setAdminNote('');
        setReceivedAmount(defaultAmount || 0);
    };

    const handleConfirmAction = async () => {
        if (!actionModal.id || !actionModal.type) return;

        setLoading(true);
        try {
            if (actionModal.type === 'approve') {
                await approvePaymentRequest_Admin(actionModal.id, adminNote, receivedAmount);
            } else {
                await rejectPaymentRequest_Admin(actionModal.id, adminNote);
            }
            message.success(`${actionModal.type === 'approve' ? 'Duyệt' : 'Từ chối'} yêu cầu thành công`);
            fetchData();
            setActionModal({ open: false, type: null, id: null });
        } catch (error) {
            // Error managed by API helper
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="p-6 bg-gray-50 min-h-screen">
            <Helmet>
                <title>Quản lý Giao dịch Đầu tư - Admin</title>
            </Helmet>

            <div className="mb-6">
                <h1 className="text-2xl font-bold text-gray-900">Giao dịch Đầu tư</h1>
                <p className="text-gray-500 mt-1">Quản lý và phê duyệt các yêu cầu nạp tiền đầu tư từ người dùng</p>
            </div>

            <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm mb-6">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex-1 flex gap-2">
                        <Input
                            placeholder="Tìm theo mã giao dịch..."
                            prefix={<Search className="w-4 h-4 text-gray-400" />}
                            className="max-w-xs"
                            value={searchCode}
                            onChange={e => setSearchCode(e.target.value)}
                            onPressEnter={handleSearch}
                        />
                        <Button
                            type="primary"
                            onClick={handleSearch}
                            className="bg-primary hover:bg-primary/90"
                        >
                            Tìm kiếm
                        </Button>
                    </div>
                    <Button
                        icon={<RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />}
                        onClick={fetchData}
                        disabled={loading}
                    >
                        Làm mới
                    </Button>
                </div>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                <Spin spinning={loading}>
                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="bg-gray-50 border-b border-gray-200">
                                    <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Người dùng</th>
                                    <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Gói</th>
                                    <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Số tiền nạp</th>
                                    <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Mã giao dịch</th>
                                    <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Ngày tạo</th>
                                    <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Trạng thái</th>
                                    <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Hành động</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {data?.data?.data.map((item) => (
                                    <tr key={item.id} className="hover:bg-gray-50 transition-colors">
                                        <td className="px-6 py-4">
                                            <div className="flex items-center">
                                                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold mr-3">
                                                    {item.user_info.avatar_url ? (
                                                        <img src={item.user_info.avatar_url} alt="" className="w-full h-full rounded-full object-cover" />
                                                    ) : (
                                                        item.user_info.fullname?.charAt(0) || 'U'
                                                    )}
                                                </div>
                                                <div>
                                                    <div className="text-sm font-medium text-gray-900">{item.user_info.fullname}</div>
                                                    <div className="text-xs text-gray-500">{item.user_info.email}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="text-sm font-medium text-gray-900">{item.package_info?.package_name || 'N/A'}</div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="text-sm font-bold text-gray-900">
                                                {formatCurrency(item.package_info?.paid_so_far)}
                                            </div>
                                            {item.package_info?.remaining && item.package_info.remaining > 0 && (
                                                <div className="text-[10px] text-orange-500">
                                                    Còn lại: {formatCurrency(item.package_info.remaining)}
                                                </div>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-500 font-mono">
                                            {item.payment_code}
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-500">
                                            {item.created_at}
                                        </td>
                                        <td className="px-6 py-4">
                                            <StatusBadge status={item.status} />
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-2">
                                                <Button
                                                    size="small"
                                                    icon={<Eye className="w-4 h-4" />}
                                                    onClick={() => handleOpenDetail(item)}
                                                />
                                                {item.status === 'pending' && (
                                                    <>
                                                        <Button
                                                            size="small"
                                                            className="text-emerald-600 hover:text-emerald-700 border-emerald-200 hover:border-emerald-300 bg-emerald-50"
                                                            icon={<Check className="w-4 h-4" />}
                                                            onClick={() => handleOpenAction('approve', item.id, item.package_info?.paid_so_far)}
                                                        />
                                                        <Button
                                                            size="small"
                                                            className="text-red-600 hover:text-red-700 border-red-200 hover:border-red-300 bg-red-50"
                                                            icon={<X className="w-4 h-4" />}
                                                            onClick={() => handleOpenAction('reject', item.id)}
                                                        />
                                                    </>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                {(!data?.data?.data || data.data.data.length === 0) && (
                                    <tr>
                                        <td colSpan={6} className="px-6 py-10 text-center text-gray-500">
                                            Không có yêu cầu giao dịch nào
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                    <div className="px-6 py-4 flex items-center justify-between border-t border-gray-200">
                        <div className="text-sm text-gray-500">
                            Hiển thị {data?.data?.from || 0} đến {data?.data?.to || 0} trong tổng số {data?.data?.total || 0}
                        </div>
                        <Pagination
                            current={page}
                            pageSize={pageSize}
                            total={data?.data?.total || 0}
                            onChange={(p, s) => {
                                setPage(p);
                                setPageSize(s);
                            }}
                            showSizeChanger
                        />
                    </div>
                </Spin>
            </div>

            {/* Action Modal */}
            <Modal
                title={actionModal.type === 'approve' ? 'Phê duyệt yêu cầu' : 'Từ chối yêu cầu'}
                open={actionModal.open}
                onOk={handleConfirmAction}
                onCancel={() => setActionModal({ open: false, type: null, id: null })}
                confirmLoading={loading}
                okText="Xác nhận"
                cancelText="Hủy"
                okButtonProps={{
                    className: actionModal.type === 'approve' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-red-600 hover:bg-red-700'
                }}
            >
                <div className="space-y-4 py-2">
                    {actionModal.type === 'approve' && (
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Số tiền thực nhận</label>
                            <InputNumber
                                className="!w-full"
                                formatter={value => `đ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                parser={value => {
                                    const parsedValue = value!.replace(/\đ\s?|(,*)/g, '');
                                    return Number(parsedValue);
                                }}
                                value={receivedAmount}
                                onChange={val => setReceivedAmount(val || 0)}
                            />
                        </div>
                    )}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Ghi chú của Admin</label>
                        <TextArea
                            rows={4}
                            placeholder="Nhập ghi chú..."
                            value={adminNote}
                            onChange={e => setAdminNote(e.target.value)}
                        />
                    </div>
                </div>
            </Modal>

            {/* Detail Modal */}
            <Modal
                title="Chi tiết giao dịch đầu tư"
                open={detailModal.open}
                onCancel={() => setDetailModal({ open: false, data: null })}
                footer={[
                    <Button key="close" onClick={() => setDetailModal({ open: false, data: null })}>
                        Đóng
                    </Button>
                ]}
                width={700}
            >
                {detailModal.data && (
                    <div className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="bg-gray-50 p-4 rounded-lg">
                                <label className="text-[10px] uppercase font-bold text-gray-400 block mb-2">Thông tin người dùng</label>
                                <div className="space-y-2">
                                    <div className="flex items-center gap-2">
                                        <User className="w-4 h-4 text-gray-400" />
                                        <span className="text-sm font-medium">{detailModal.data.user_info.fullname}</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <FileText className="w-4 h-4 text-gray-400" />
                                        <span className="text-sm text-gray-600">{detailModal.data.user_info.email}</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Tag className="w-4 h-4 text-gray-400" />
                                        <span className="text-sm text-gray-600 font-mono text-xs">{detailModal.data.payment_code}</span>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-gray-50 p-4 rounded-lg">
                                <label className="text-[10px] uppercase font-bold text-gray-400 block mb-2">Thông tin gói đầu tư</label>
                                <div className="space-y-2">
                                    <div className="flex items-center gap-2">
                                        <Package className="w-4 h-4 text-gray-400" />
                                        <span className="text-sm font-medium">{detailModal.data.package_info?.package_name}</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Calendar className="w-4 h-4 text-gray-400" />
                                        <span className="text-sm text-gray-600">{dayjs(detailModal.data.created_at).format('DD/MM/YYYY HH:mm')}</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <div className="w-4 h-4 flex items-center justify-center">
                                            <div className="w-2 h-2 rounded-full bg-primary" />
                                        </div>
                                        <StatusBadge status={detailModal.data.status} />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="border border-gray-100 p-4 rounded-lg text-center">
                                <div className="text-xs text-gray-500 mb-1">Giá trị gói</div>
                                <div className="text-lg font-bold text-gray-900">{formatCurrency(detailModal.data.package_info?.total_price)}</div>
                            </div>
                            <div className="border border-gray-100 p-4 rounded-lg text-center bg-primary/5">
                                <div className="text-xs text-primary/70 mb-1">Số tiền đã nạp</div>
                                <div className="text-lg font-bold text-primary">{formatCurrency(detailModal.data.package_info?.paid_so_far)}</div>
                            </div>
                            <div className="border border-gray-100 p-4 rounded-lg text-center">
                                <div className="text-xs text-gray-500 mb-1">Số dư còn lại</div>
                                <div className="text-lg font-bold text-gray-900">{formatCurrency(detailModal.data.package_info?.remaining)}</div>
                            </div>
                        </div>

                        {detailModal.data.image_url && (
                            <div className="mt-4">
                                <label className="text-[10px] uppercase font-bold text-gray-400 block mb-2 flex items-center gap-2">
                                    <ImageIcon className="w-3 h-3" /> Bằng chứng thanh toán
                                </label>
                                <div className="border border-gray-200 rounded-lg overflow-hidden max-h-80 bg-gray-100 flex items-center justify-center">
                                    <img 
                                        src={detailModal.data.image_url} 
                                        alt="Evidence" 
                                        className="max-w-full max-h-80 object-contain cursor-pointer hover:opacity-90 transition-opacity"
                                        onClick={() => window.open(detailModal.data.image_url, '_blank')}
                                    />
                                </div>
                            </div>
                        )}

                        {detailModal.data.user_note && (
                            <div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
                                <label className="text-[10px] uppercase font-bold text-blue-400 block mb-1">Ghi chú từ người dùng</label>
                                <p className="text-sm text-blue-800 italic">"{detailModal.data.user_note}"</p>
                            </div>
                        )}
                    </div>
                )}
            </Modal>
        </div>
    );
}